import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

type ComboItemInput = {
  productId?: string;
  productName?: string;
  quantity: number;
};

/**
 * Reemplaza por completo los componentes de un combo existente.
 *
 * El combo (`productId` apuntado) debe ser de tipo "combo". Los componentes
 * pueden referenciarse por `productId` (preferido) o `productName` (match
 * exacto, case-insensitive). No se permiten combos anidados ni listas vacías.
 *
 * Endpoint: PUT /restaurants/:restaurantId/products/:productId/combo-items
 */
export const setComboItemsSkill: Skill = {
  id: "setComboItems",
  description:
    "Reemplaza los componentes (productos hijos) de un combo existente. Úsala cuando el usuario quiera " +
    "cambiar, añadir o quitar productos de un combo ya creado. Si el producto NO es de tipo combo, falla. " +
    "Se reemplaza la lista completa (no añade incremental — pasa SIEMPRE todos los componentes que deben quedar).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string", description: "ID del restaurante" },
      productId: {
        type: "string",
        description: "ID del producto combo a modificar",
      },
      items: {
        type: "array",
        description:
          "Lista COMPLETA de componentes finales del combo (no incremental). Mínimo 1.",
        items: {
          type: "object",
          properties: {
            productId: { type: "string" },
            productName: { type: "string" },
            quantity: { type: "integer", minimum: 1 },
          },
          required: ["quantity"],
        },
      },
    },
    required: ["restaurantId", "productId", "items"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const comboId = String(args.productId ?? "");
    const rawItems = Array.isArray(args.items)
      ? (args.items as ComboItemInput[])
      : [];

    if (!restaurantId || !comboId) {
      return { ok: false, error: "Faltan restaurantId o productId." };
    }
    if (rawItems.length === 0) {
      return {
        ok: false,
        error:
          "Un combo no puede quedar vacío. Pasa al menos 1 componente o usa archiveProduct si lo quieres eliminar.",
      };
    }

    // ── Cargar catálogo y producto combo ──────────────────────────────────
    let catalog: Array<{
      id: string;
      name: string;
      type?: string;
      isArchived?: boolean;
    }> = [];
    let combo: { id: string; name: string; type?: string } | undefined;
    try {
      const [comboRes, listRes] = await Promise.all([
        apiWithAuth.get(
          `/restaurants/${restaurantId}/products/${comboId}`,
          ctx.accessToken,
        ),
        apiWithAuth.get(
          `/restaurants/${restaurantId}/products?limit=500&includeArchived=false`,
          ctx.accessToken,
        ),
      ]);
      combo = comboRes.data;
      catalog = (listRes.data?.data ?? listRes.data?.items ?? listRes.data ?? []) as typeof catalog;
    } catch (e: any) {
      return {
        ok: false,
        error:
          "No se pudo cargar el combo o catálogo: " +
          (e?.message ?? "error desconocido"),
      };
    }
    if (!combo) return { ok: false, error: "Combo no encontrado." };
    if (combo.type !== "combo") {
      return {
        ok: false,
        error: `"${combo.name}" no es un combo (tipo: ${combo.type}). Solo combos aceptan setComboItems.`,
      };
    }

    // ── Resolver y validar items ──────────────────────────────────────────
    const byId = new Map(catalog.map((p) => [p.id, p]));
    const byName = new Map(
      catalog.map((p) => [p.name.trim().toLowerCase(), p]),
    );
    const resolved: Array<{ productId: string; name: string; quantity: number }> =
      [];
    const errors: string[] = [];
    for (const raw of rawItems) {
      const qty = Math.max(1, Math.floor(Number(raw.quantity)));
      if (!Number.isFinite(qty) || qty < 1) {
        errors.push("Cantidad inválida en componente.");
        continue;
      }
      let match: { id: string; name: string; type?: string } | undefined;
      if (raw.productId) {
        match = byId.get(String(raw.productId));
        if (!match) {
          errors.push(`Componente productId="${raw.productId}" no existe.`);
          continue;
        }
      } else if (raw.productName) {
        match = byName.get(String(raw.productName).trim().toLowerCase());
        if (!match) {
          errors.push(
            `Componente "${raw.productName}" no se encontró por nombre exacto.`,
          );
          continue;
        }
      } else {
        errors.push("Componente sin productId ni productName.");
        continue;
      }
      if (match.id === comboId) {
        errors.push("Un combo no puede contenerse a sí mismo.");
        continue;
      }
      if (match.type === "combo") {
        errors.push(
          `"${match.name}" es un combo. No se permiten combos dentro de combos.`,
        );
        continue;
      }
      resolved.push({ productId: match.id, name: match.name, quantity: qty });
    }
    if (errors.length > 0) {
      return { ok: false, error: errors.join(" ") };
    }

    // ── Confirmación bloqueante ──────────────────────────────────────────
    const ok = await ctx.confirmation.confirm({
      title: "Actualizar componentes del combo",
      description: `Vas a reemplazar la composición de "${combo.name}". El stock del combo se recalculará automáticamente.`,
      details: [
        { label: "Combo", value: combo.name },
        {
          label: "Nueva composición",
          value: resolved.map((i) => `${i.quantity}× ${i.name}`).join(", "),
        },
      ],
      confirmLabel: "Guardar",
    });
    if (!ok) {
      return { ok: false, error: "Acción cancelada por el usuario." };
    }

    // ── API call ─────────────────────────────────────────────────────────
    try {
      await apiWithAuth.put(
        `/restaurants/${restaurantId}/products/${comboId}/combo-items`,
        {
          items: resolved.map((i, idx) => ({
            productId: i.productId,
            quantity: i.quantity,
            sortOrder: (idx + 1) * 10,
          })),
        },
        ctx.accessToken,
      );

      emitCopilotMutation({
        scope: "products",
        action: "updated",
        restaurantId,
        id: comboId,
        meta: { name: combo.name },
      });

      return {
        ok: true,
        result: {
          comboId,
          items: resolved.map((i) => ({
            productId: i.productId,
            name: i.name,
            quantity: i.quantity,
          })),
        },
        humanSummary: `Componentes de "${combo.name}" actualizados: ${resolved
          .map((i) => `${i.quantity}× ${i.name}`)
          .join(", ")}.`,
      };
    } catch (err: any) {
      const detail =
        err?.response?.data?.message ||
        err?.message ||
        "Error actualizando componentes del combo.";
      return { ok: false, error: detail };
    }
  },
};
